export const COEXISTENCE_STATES = ['OFFBOARDED', 'DISCONNECTED'] as const;

export type CoexistenceState = (typeof COEXISTENCE_STATES)[number];

export interface CoexistenceNotice {
  state: CoexistenceState;
  tone: 'info' | 'error';
  headline: string;
  guidance: string;
  cause: string | null;
  reconnect: { label: string; to: string } | null;
}

export const COEXISTENCE_RECONNECT_PATH = '/home/integrations/whatsapp-business';

export const COEXISTENCE_REASON_COPY: Record<string, string> = {
  PRIMARY_INACTIVITY:
    'the WhatsApp Business app on the main phone had not been opened for about two weeks',
  COMPANION_INACTIVITY:
    'this number had been inactive on its linked devices for about a month',
  USER_RE_REGISTERED: 'WhatsApp was re-registered on a new device',
  CHANGE_NUMBER: 'the WhatsApp account was moved to a different phone number',
  ACCOUNT_DISCONNECTED:
    'the link was removed from the WhatsApp Business app',
  BUSINESS_DOWNGRADE:
    'the WhatsApp Business account was changed back to a personal account',
};

export function normalizeCoexistenceState(
  value: string | null | undefined
): CoexistenceState | null {
  return (COEXISTENCE_STATES as readonly string[]).includes(value as string)
    ? (value as CoexistenceState)
    : null;
}

export function isCoexistenceDegraded(
  value: string | null | undefined
): boolean {
  return normalizeCoexistenceState(value) !== null;
}

export function describeCoexistenceReason(
  reason: string | null | undefined
): string | null {
  if (typeof reason !== 'string') return null;
  const key = reason.trim().toUpperCase();
  if (!key) return null;
  return COEXISTENCE_REASON_COPY[key] ?? null;
}

function accountLabel(
  accountName?: string | null,
  phoneNumber?: string | null
): string {
  if (accountName && phoneNumber) return `${accountName} (${phoneNumber})`;
  return accountName || phoneNumber || 'Your WhatsApp number';
}

export interface BuildCoexistenceNoticeInput {
  state?: string | null;
  reason?: string | null;
  accountName?: string | null;
  phoneNumber?: string | null;
}

export function buildCoexistenceNotice({
  state,
  reason,
  accountName,
  phoneNumber,
}: BuildCoexistenceNoticeInput): CoexistenceNotice | null {
  const normalized = normalizeCoexistenceState(state);
  if (!normalized) return null;

  const label = accountLabel(accountName, phoneNumber);
  const cause = describeCoexistenceReason(reason);

  if (normalized === 'OFFBOARDED') {
    return {
      state: normalized,
      tone: 'info',
      headline: `${label} is restoring its WhatsApp connection.`,
      guidance:
        "Restoring automatically, usually within minutes. Finish re-registering WhatsApp on your phone and keep the WhatsApp Business app open — and don't link this number to another provider, or the restore is cancelled.",
      cause,
      reconnect: null,
    };
  }

  return {
    state: normalized,
    tone: 'error',
    headline: `${label} is disconnected from WhatsApp.`,
    guidance: cause
      ? `Sending is paused because ${cause}. Reconnect this number to start sending again.`
      : "Sending is paused — Meta ended this number's connection. Reconnect this number to start sending again.",
    cause,
    reconnect: { label: 'Reconnect', to: COEXISTENCE_RECONNECT_PATH },
  };
}
